import { useNavigate } from "react-router";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import type { ImageDto } from "@/api/types";
import bgPlaceholder from "@/assets/images/image-placeholder.png";
import { Award01Icon } from "@/components/Icons/Award01Icon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { PlusIcon } from "@/components/Icons/PlusIcon";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { ContentBlock } from "@/design-system/ContentBlock";
import { Inline } from "@/design-system/Inline";
import { ToggleButton } from "@/design-system/ToggleButton";
import { useSlug } from "@/hooks/useSlug";
import { communityGroupMutations } from "@/queries/community-groups/mutations";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";
import { RuntimeBackgroundImageLayer } from "@/ui-adapters/RuntimeBackgroundImage";

interface PromotedGroupProps {
  id: string;
  name: string;
  description: string;
  image?: ImageDto;
  isReadonly?: boolean;
  isResidentGroup?: boolean;
  isOwner?: boolean;
  isMember?: boolean;
  followCallback?: (id: string, action: "join" | "leave") => void;
}
export function PromotedGroupCard({
  id,
  name,
  description,
  image,
  isReadonly,
  isResidentGroup,
  isOwner,
  isMember,
  followCallback,
}: PromotedGroupProps): React.ReactNode {
  const slug = useSlug();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const resolveImage = useImageResolver();
  const followMutation = communityGroupMutations.useFollowGroup(undefined);
  const unfollowMutation = communityGroupMutations.useUnfollowGroup(undefined);

  const onClickToggleFollow = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isMember) {
      unfollowMutation.mutate(
        { id, groupType: "interest" },
        {
          onSuccess: () => followCallback?.(id, "leave"),
        },
      );
    } else {
      followMutation.mutate(
        { id, groupType: "interest" },
        {
          onSuccess: () => followCallback?.(id, "join"),
        },
      );
    }
  };

  return (
    <ContentBlock width="compactPreview">
      <Card
        as="article"
        density="flush"
        clip
        interactive={!isReadonly}
        onClick={
          isReadonly
            ? undefined
            : () =>
                void navigate(
                  routes.interestGroups.details({
                    slug,
                    id,
                  }),
                )
        }
      >
        <RuntimeBackgroundImageLayer
          imageUrl={image ? resolveImage(image, "intrinsic") : bgPlaceholder}
          overlay="darkScrim"
        >
          {(runtimeBackgroundAttributes) => (
            <section
              {...runtimeBackgroundAttributes}
              className="flex h-card-media w-full flex-col items-center justify-center gap-space-sm rounded-t-xl bg-background-tertiary bg-cover bg-center p-space-sm"
            >
              <span className="text-center text-heading-3 text-text-default-inverse">{name}</span>
              <Inline gap="xs" align="center">
                {isOwner && isResidentGroup ? (
                  <Button data-testid="group-admin-btn" icon={<Award01Icon />} disabled={true}>
                    {t("model.interest-group.owner")}
                  </Button>
                ) : (
                  <ToggleButton
                    config={{
                      checked: {
                        icon: <CheckIcon />,
                        label: t("model.interest-group.action.unfollow"),
                      },
                      unchecked: {
                        icon: <PlusIcon />,
                        label: t("model.interest-group.action.follow"),
                      },
                    }}
                    onClick={onClickToggleFollow}
                    disabled={isReadonly}
                    isChecked={isMember}
                  />
                )}
              </Inline>
            </section>
          )}
        </RuntimeBackgroundImageLayer>
        <span className="p-space-md text-caption whitespace-pre-wrap">{description}</span>
      </Card>
    </ContentBlock>
  );
}
